import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowRight } from "lucide-react";
import Logo from "../components/Logo";
import { GoogleLogin } from "@react-oauth/google";
import api from "../lib/api";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setErr(""); setLoading(true);
    try {
      await api.post('/auth/google', { token: credentialResponse.credential });
      // If your AuthContext exposes a way to refresh user state (like login does internally), 
      // you might need to call it or just navigate which might trigger a refetch if needed.
      // We will assume the cookie is set and we can just navigate.
      navigate(from, { replace: true });
      window.location.reload(); // Refresh to ensure auth context picks up the new cookie
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Google Login failed");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:block relative crest-bg text-white">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="aurora opacity-60" />
        <div className="relative h-full flex flex-col p-12 justify-between">
          <Logo to="/" />
          <div>
            <h2 className="font-display text-4xl tracking-tighter leading-tight max-w-md text-white">
              Practice today. Pass <span className="font-serif text-gradient-gold">tomorrow.</span> Get refunded if you don't.
            </h2>
            <p className="mt-4 text-white/70 max-w-md">
              184,000+ engineers are already practicing with BeforeExam. Welcome back.
            </p>
          </div>
          <div className="text-sm text-white/50">© BeforeExam Labs</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <h1 className="font-display text-3xl tracking-tight">Sign in to BeforeExam</h1>
          <p className="text-sm text-muted-foreground mt-1">Resume your streak, see your readiness, keep practicing.</p>

          <div className="mt-6 w-full flex justify-center" data-testid="google-login-button">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => setErr('Google Login Failed')}
              theme="outline"
              size="large"
              width="100%"
              text="continue_with"
            />
          </div>

          <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px bg-border flex-1" /> or continue with email <div className="h-px bg-border flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 h-11 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-foreground"
                data-testid="login-email" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 h-11 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-foreground"
                data-testid="login-password" />
            </div>
            {err && <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive" data-testid="login-error">{err}</div>}
            <Button disabled={loading} type="submit" className="h-11 w-full btn-primary" data-testid="login-submit">
              {loading ? "Signing in…" : <>Sign in <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            New here?{" "}
            <Link to="/register" className="font-semibold text-foreground hover:underline" data-testid="link-to-register">Create your free account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
